import { Button, Icon, useToast } from '@chakra-ui/react';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserService } from 'services/user/user.service';

interface ExportUsersButtonProps {
  /** SUPER_ADMIN only: org id to export. Omit → export the current org from the token (ADMIN). */
  org?: string;
  isDisabled?: boolean;
}

/**
 * Export the organization's users to a .csv download (GET /users/export).
 * Rendered on the Company Users tab header for ADMIN (own org) and SUPER_ADMIN (any org).
 */
const ExportUsersButton = ({ org, isDisabled }: ExportUsersButtonProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await UserService.exportUsers(org);
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid export response');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: t('managementSystem.companyDetail.exportUsersError', {
          defaultValue: 'Failed to export users. Please try again.',
        }),
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      isDisabled={isDisabled}
      isLoading={isExporting}
      leftIcon={<Icon as={Download} boxSize='14px' />}
      onClick={handleExport}
      size='ds-sm'
      variant='ds-primary'
    >
      {t('managementSystem.companyDetail.exportUsers', { defaultValue: 'Export users' })}
    </Button>
  );
};

export default ExportUsersButton;
